/**
 * @file useChatUrlParam.ts
 * @description Адрес окна «Сообщения» (chat-window-design.md §6 «Адреса»):
 * `?chat=<id>` на любой странице магазина открывает окно на диалоге,
 * `?chat=open` — на списке, `&chatCustomer=<id>` добавляет отбор по клиенту.
 * Параметры затем убираются из адреса (замена записи истории, без шага).
 * Нет права переписки — «Нет доступа к переписке с клиентами».
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import { useNotify } from "../ui/NotifyProvider";
import type { OpenChatTarget } from "./chat-window-context";
import { CHAT_TEXT } from "./chat-texts";

/** Параметры адреса окна. */
export const CHAT_PARAM = "chat";
export const CHAT_CUSTOMER_PARAM = "chatCustomer";
/** Значение `?chat=` «просто открыть окно». */
export const CHAT_OPEN_VALUE = "open";

/** Адрес «открыть окно» на текущей странице: `?chat=<id>&chatCustomer=<id>`. */
export function chatSearch(target: { conversationId?: string; customerId?: string }): string {
  const q = new URLSearchParams();
  q.set(CHAT_PARAM, target.conversationId ?? CHAT_OPEN_VALUE);
  if (target.customerId) q.set(CHAT_CUSTOMER_PARAM, target.customerId);
  return `?${q.toString()}`;
}

/** Разбор параметров адреса; null — окно открывать не нужно. */
export function parseChatSearch(search: string): OpenChatTarget | null {
  const q = new URLSearchParams(search);
  const chat = q.get(CHAT_PARAM);
  if (chat === null) return null;
  const customerId = q.get(CHAT_CUSTOMER_PARAM) ?? undefined;
  const conversationId = chat && chat !== CHAT_OPEN_VALUE ? chat : undefined;
  return { conversationId, customerId };
}

export function useChatUrlParam(enabled: boolean, openChat: (target: OpenChatTarget) => void) {
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();

  useEffect(() => {
    const target = parseChatSearch(location.search);
    if (!target) return;
    const q = new URLSearchParams(location.search);
    q.delete(CHAT_PARAM);
    q.delete(CHAT_CUSTOMER_PARAM);
    const rest = q.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash }, { replace: true });
    if (!enabled) {
      notify.error(CHAT_TEXT.noAccess);
      return;
    }
    // Окно открываем после замены адреса: иначе замена сотрёт шаг истории
    // окна. Таймер не снимаем при перезапуске эффекта — перезапуск вызовет
    // сама замена адреса.
    window.setTimeout(() => openChat(target), 0);
  }, [location.search, location.pathname, location.hash, enabled, openChat, navigate, notify]);
}
